import React, { useState } from 'react'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useTheme } from '@/context/ThemeContext'
import { useChatStore } from '../chatStore'
import ProductSelect from './ProductSelect'
import { TimeWindowSelect } from '@/components/chat/TimeWindowSelect'
import SuggestedTopics from '@/components/chat/SuggestedTopics'

const suggestedTopics = [
  { id: 't1', text: 'Identify key personas & user types' },
  { id: 't2', text: 'Describe our users typical day' },
  { id: 't3', text: 'Name users top pain points' },
  { id: 't4', text: 'List recent feature requests' },
]

function getThreadName(input: string) {
  return input.split('\n')[0].slice(0, 50)
}

export default function NewChatCompose() {
  const { isDarkMode } = useTheme()
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { selectedProduct, timeWindow, setTimeWindow, addToNewChatThread } = useChatStore()

  const handleSubmit = async () => {
    if (!input.trim() || !selectedProduct || isSubmitting) return

    setIsSubmitting(true)

    await addToNewChatThread({
      productId: selectedProduct.id,
      name: getThreadName(input),
      message: input,
    })

    setIsSubmitting(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleSelectTopic = async (topic: string) => {
    setIsSubmitting(true)

    await addToNewChatThread({
      productId: selectedProduct.id,
      name: getThreadName(topic),
      message: topic,
    })

    setIsSubmitting(false)
  }

  return (
    <div className="h-full w-full overflow-y-auto pt-[70px] flex items-center justify-center">
      <div className="flex flex-col items-center -translate-y-[50px] -translate-x-[50px]">
        <h1 className={`text-3xl font-bold mb-8 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          How can I help with your product today?
        </h1>

        <div className="w-full max-w-2xl relative">
          {/* Gradient background with animation */}
          <div
            className={`absolute -inset-[1px] rounded-xl bg-gradient-to-r from-[#FF4A8E] via-[#8B5CF6] to-[#FFC921] opacity-75 blur-[3px]
              animate-gradient-xy ${isDarkMode ? 'opacity-30' : 'opacity-15'}`}
          />

          <Card className={`w-full relative ${isDarkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95'}`}>
            <CardContent className="p-6">
              <div className="relative mb-4">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything about your product..."
                  className={`min-h-[120px] resize-none ${
                    isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                />
                <Button
                  onClick={handleSubmit}
                  disabled={!input.trim() || isSubmitting}
                  className="absolute bottom-3 right-3"
                  size="sm"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <ProductSelect />
                </div>
                <div className="w-40">
                  <TimeWindowSelect value={timeWindow} onChange={setTimeWindow} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 w-full max-w-2xl">
          <SuggestedTopics topics={suggestedTopics} onSelectTopic={handleSelectTopic} />
        </div>
      </div>
    </div>
  )
}
